import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import config from '../../../../../auth_config.json';
import {JournalResponse, ApiResponse, ComponentSummary, ComponentDetail, ComponentType, SafetyGoal, AnalysisRow, FailureEffectOption, SystemEffectOption, FailureInstanceOption, SafetyMechanismOption, CoverageLink, ThermalModelOption, StandardOption, IdSuggestions, TelesisNetlistUploadResult, SmartPdfUploadResult, Project, DocumentMetadata } from '../models';
import { Observable } from 'rxjs';
import { PdfAnnotationResponse, WorksheetRow } from '../models';

export function sanitizeId(id: string | null | undefined): string {
  if (!id) return '';

  // If a composite ID is intercepted, print the component stack trace!
  if (/^PROJ-[A-Za-z0-9]+_/.test(id)) {
    console.warn(`⚠️ [COMPOSITE ID DETECTED] Stripping prefix from: "${id}"`);
    console.trace(); // 👈 This logs the EXACT file name & line number in Browser DevTools!
    return id.replace(/^PROJ-[A-Za-z0-9]+_/, '');
  }

  return id;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = config.fmedaApiUri;

  getProjectJournal(projectId: string, beginning: number = 0, end: number = 50): Observable<JournalResponse> {
    const params = new HttpParams()
      .set('beginning', beginning.toString())
      .set('end', end.toString());

    return this.http.get<JournalResponse>(`${this.apiBaseUrl}/api/projects/${projectId}/journal`, { params });
  }

  getComponents() {
    return this.http.get<ApiResponse<ComponentSummary[]>>(`${this.apiBaseUrl}/api/components`);
  }

  getComponentDetail(componentDesignator: string) {
    return this.http.get<ApiResponse<ComponentDetail>>(`${this.apiBaseUrl}/api/components/${encodeURIComponent(componentDesignator)}`);
  }

  getComponentTypes() {
    return this.http.get<ApiResponse<ComponentType[]>>(`${this.apiBaseUrl}/api/component-types`);
  }

  getSafetyGoals() {
    return this.http.get<ApiResponse<SafetyGoal[]>>(`${this.apiBaseUrl}/api/safety-goals`);
  }

  getAnalysis() {
    return this.http.get<ApiResponse<AnalysisRow[]>>(`${this.apiBaseUrl}/api/analysis`);
  }

  getWorksheet() {
    return this.http.get<ApiResponse<WorksheetRow[]>>(`${this.apiBaseUrl}/api/analysis/worksheet`);
  }

  getFailureEffects() {
    return this.http.get<ApiResponse<FailureEffectOption[]>>(`${this.apiBaseUrl}/api/failure-effects`);
  }

  getSystemEffects() {
    return this.http.get<ApiResponse<SystemEffectOption[]>>(`${this.apiBaseUrl}/api/system-effects`);
  }

  getFailureInstances() {
    return this.http.get<ApiResponse<FailureInstanceOption[]>>(`${this.apiBaseUrl}/api/failure-instances`);
  }

  deleteFailureInstance(failureInstanceId: string) {
    return this.http.delete<ApiResponse<{ message: string; failure_instance_id: string }>>(
      `${this.apiBaseUrl}/api/failure-instances/${encodeURIComponent(sanitizeId(failureInstanceId))}`
    );
  }

  getSafetyMechanisms() {
    return this.http.get<ApiResponse<SafetyMechanismOption[]>>(`${this.apiBaseUrl}/api/safety-mechanisms`);
  }

  getCoverageLinks() {
    return this.http.get<ApiResponse<CoverageLink[]>>(`${this.apiBaseUrl}/api/coverage-links`);
  }

  getThermalModels() {
    return this.http.get<ApiResponse<ThermalModelOption[]>>(`${this.apiBaseUrl}/api/thermal-models`);
  }

  getStandards() {
    return this.http.get<ApiResponse<StandardOption[]>>(`${this.apiBaseUrl}/api/standards`);
  }

  getIdSuggestions() {
    return this.http.get<ApiResponse<IdSuggestions>>(`${this.apiBaseUrl}/api/id-suggestions`);
  }

  createComponentType(payload: any) {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/component-types`, payload);
  }

  createComponent(payload: any) {
    const cleanPayload = typeof payload === 'object' && payload ? {
      ...payload,
      id: payload.id ? sanitizeId(payload.id) : undefined
    } : payload;

    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/components`, cleanPayload);
  }

  createFailureLink(payload: any) {
    const cleanPayload = typeof payload === 'object' && payload ? {
      ...payload,
      failure_effect_id: payload.failure_effect_id ? sanitizeId(payload.failure_effect_id) : undefined,
      system_effect_id: payload.system_effect_id ? sanitizeId(payload.system_effect_id) : undefined,
      fi_id: payload.fi_id ? sanitizeId(payload.fi_id) : undefined
    } : payload;

    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/failure-links`, cleanPayload);
  }

  createFailureInstance(payload: { component_designator: string; failure_mode_code: string; fi_name: string; fi_id?: string; }) {
    const cleanPayload = {
      ...payload,
      fi_id: payload.fi_id ? sanitizeId(payload.fi_id) : undefined
    };

    return this.http.post<ApiResponse<{ message: string; failure_instance_id: string }>>(
      `${this.apiBaseUrl}/api/failure-instances`,
      cleanPayload
    );
  }

  createSafetyMechanism(payload: any) {
    const cleanPayload = typeof payload === 'object' && payload ? {
      ...payload,
      sm_id: payload.sm_id ? sanitizeId(payload.sm_id) : undefined,
      failure_instance_id: payload.failure_instance_id ? sanitizeId(payload.failure_instance_id) : undefined
    } : payload;

    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/safety-mechanisms`, cleanPayload);
  }

  createSafetyGoal(payload: { description: string; id?: string }) {
    const cleanPayload = {
      ...payload,
      id: payload.id ? sanitizeId(payload.id) : undefined
    };

    return this.http.post<ApiResponse<{ message: string; id: string }>>(
      `${this.apiBaseUrl}/api/safety-goals`,
      cleanPayload
    );
  }

  updateSafetyGoal(safetyGoalId: string, description: string) {
    return this.http.patch<ApiResponse<{ message: string; id: string }>>(
      `${this.apiBaseUrl}/api/safety-goals/${encodeURIComponent(sanitizeId(safetyGoalId))}`,
      { description }
    );
  }

  createSystemEffect(payload: { name: string; safety_goal_id: string; id?: string }) {
    const cleanPayload = {
      ...payload,
      safety_goal_id: sanitizeId(payload.safety_goal_id),
      id: payload.id ? sanitizeId(payload.id) : undefined
    };

    return this.http.post<ApiResponse<{ message: string; id: string }>>(
      `${this.apiBaseUrl}/api/system-effects`,
      cleanPayload
    );
  }

  createFailureEffect(payload: { name: string; system_effect_id: string; id?: string }) {
    const cleanPayload = {
      ...payload,
      system_effect_id: sanitizeId(payload.system_effect_id),
      id: payload.id ? sanitizeId(payload.id) : undefined
    };

    return this.http.post<ApiResponse<{ message: string; id: string }>>(
      `${this.apiBaseUrl}/api/failure-effects`,
      cleanPayload
    );
  }

  updateCoverage(value: { safetyMechanismId: string; failureInstanceId: string; diagnosticCoverage: number; mechanismType?: string }) {
    return this.http
      .patch<ApiResponse<{ safety_mechanism_id: string; failure_instance_id: string; diagnostic_coverage: number }>>(
        `${this.apiBaseUrl}/api/safety-mechanisms/${encodeURIComponent(sanitizeId(value.safetyMechanismId))}/coverage`,
        {
          failure_instance_id: sanitizeId(value.failureInstanceId),
          diagnostic_coverage: value.diagnosticCoverage,
          mechanism_type: value.mechanismType ?? 'SPF'
        }
      );
  }

  updateFailureEffectLink(value: { failureEffectId: string, systemEffectId: string }) {
    return this.http.patch<ApiResponse<FailureEffectOption>>(
      `${this.apiBaseUrl}/api/failure-effects/${encodeURIComponent(sanitizeId(value.failureEffectId))}/link`,
      { system_effect_id: sanitizeId(value.systemEffectId)}
    );
  }

  updateSystemEffectLink(value: { systemEffectId: string; safetyGoalId: string; name?: string }) {
    const payload = {
      safety_goal_id: sanitizeId(value.safetyGoalId),
      ...(value.name === undefined ? {} : { name: value.name.trim() })
    };
    return this.http.patch<
      ApiResponse<{ system_effect_id: string; safety_goal_id: string | null }>
    >(
      `${this.apiBaseUrl}/api/system-effects/${encodeURIComponent(sanitizeId(value.systemEffectId))}/link`,
      payload
    );
  }

  updateFailureInstanceCause(value: { failureInstanceId: string; failureEffectId: string }) {
    return this.http.patch<ApiResponse<unknown>>(
      `${this.apiBaseUrl}/api/failure-instances/${encodeURIComponent(sanitizeId(value.failureInstanceId))}/cause`,
      { failure_effect_id: sanitizeId(value.failureEffectId) }
    );
  }

  updateComponentTemp(value: { componentDesignator: string; opTemp: number }) {
    return this.http
      .patch<ApiResponse<{ component_id: string; op_temp: number }>>(
        `${this.apiBaseUrl}/api/components/${encodeURIComponent(value.componentDesignator)}`,
        {
          op_temp: value.opTemp
        }
      );
  }

 createLink(payload: { source_id: string; target_id: string; rel_type: string }) {
  const cleanPayload = {
    ...payload,
    source_id: sanitizeId(payload.source_id),
    target_id: sanitizeId(payload.target_id)
  };
  return this.http.post<ApiResponse<{ source_id: string; target_id: string; rel_type: string }>>(
    `${this.apiBaseUrl}/api/links`,
    cleanPayload
  );
}

  uploadTelesisNetlist(file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<ApiResponse<TelesisNetlistUploadResult>>(
      `${this.apiBaseUrl}/api/netlists/telesis/upload`,
      formData
    );
  }

  uploadSmartPdf(file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<ApiResponse<SmartPdfUploadResult>>(
      `${this.apiBaseUrl}/api/documents/smart-pdf/upload`,
      formData
    );
  }

  getLatestSmartPdf() {
    return this.http.get<ApiResponse<DocumentMetadata>>(`${this.apiBaseUrl}/api/documents/smart-pdf/latest`);
  }

  getSmartPdfVersions() {
    return this.http.get<ApiResponse<DocumentMetadata[]>>(`${this.apiBaseUrl}/api/documents/smart-pdf/versions`);
  }

  getSmartPdfAnnotations(filename: string) {
    return this.http.get<ApiResponse<PdfAnnotationResponse[]>>(
      `${this.apiBaseUrl}/api/documents/smart-pdf/${encodeURIComponent(filename)}/annotations`
    );
  }

  getSmartPdfFile(filename: string): Observable<Blob> {
    return this.http.get(`${this.apiBaseUrl}/api/documents/smart-pdf/${encodeURIComponent(filename)}`, {
      responseType: 'blob'
    });
  }

  assignComponentType(componentDesignator: string, componentTypeName: string, opTemp?: number) {
    const payload: { component_type_name: string; op_temp?: number } = { component_type_name: componentTypeName };
    if (opTemp !== undefined) {
      payload.op_temp = opTemp;
    }
    return this.http.patch<ApiResponse<{ id: string; component_type: string; op_temp: number }>>(
      `${this.apiBaseUrl}/api/components/${encodeURIComponent(componentDesignator)}/type`,
      payload
    );
  }

  bulkAssignComponentType(componentDesignators: string[], componentTypeName: string, opTemp?: number) {
    const payload: { component_designators: string[]; component_type_name: string; op_temp?: number } = {
      component_designators: componentDesignators,
      component_type_name: componentTypeName
    };
    if (opTemp !== undefined) {
      payload.op_temp = opTemp;
    }

    return this.http.post<ApiResponse<{
      count: number;
      component_type: string;
      components: Array<{ id: string; component_type: string; op_temp: number }>;
    }>>(`${this.apiBaseUrl}/api/components/type/bulk`, payload);
  }

  getProjects() {
    return this.http.get<ApiResponse<Project[]>>(`${this.apiBaseUrl}/api/projects`);
  }

  createProject(payload: { name: string; id?: string }, orgId?: string) {
    let url = `${this.apiBaseUrl}/api/projects`;
    if (orgId) {
      url += `?org_id=${encodeURIComponent(orgId)}`;
    }
    return this.http.post<ApiResponse<Project>>(url, payload);
  }

  renameProject(projectId: string, name: string) {
    return this.http.patch<ApiResponse<Project>>(
      `${this.apiBaseUrl}/api/projects/${encodeURIComponent(projectId)}`,
      { name }
    );
  }

  deleteProject(projectId: string) {
    return this.http.delete<ApiResponse<{ message: string }>>(
      `${this.apiBaseUrl}/api/projects/${encodeURIComponent(projectId)}`
    );
  }
  /**
   * Triggers a timeline rollback for the active project set in X-Project-ID
   */
  undoLastAction(): Observable<any> {
    return this.http.post<any>(`${this.apiBaseUrl}/api/projects/undo`, {});
  }
}
